import { useState, useRef } from 'react';
import { Megaphone, Trash2, Edit3, Move, BarChart3, GripHorizontal } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL;

export default function AnnouncementCard({ ann, isAdmin, user, onEdit, onDelete, onRefresh, free }) {
  const [dragging, setDragging] = useState(false);
  const [resizing, setResizing] = useState(false);
  const ref = useRef(null);
  const dragData = useRef({ startX: 0, startY: 0, offsetX: 0, offsetY: 0 });
  const resizeData = useRef({ startX: 0, startY: 0, startW: 0, startH: 0 });

  const handleDragStart = (e) => {
    if (!isAdmin) return;
    e.stopPropagation();
    const rect = ref.current.getBoundingClientRect();
    dragData.current = {
      startX: e.clientX,
      startY: e.clientY,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
    };
    setDragging(true);
    window.addEventListener('pointermove', handleDragMove);
    window.addEventListener('pointerup', handleDragEnd);
  };

  const handleDragMove = (e) => {
    if (!ref.current) return;
    const newX = e.clientX - dragData.current.offsetX;
    const newY = e.clientY - dragData.current.offsetY + window.scrollY;
    ref.current.style.left = `${newX}px`;
    ref.current.style.top = `${newY}px`;
  };

  const handleDragEnd = async (e) => {
    window.removeEventListener('pointermove', handleDragMove);
    window.removeEventListener('pointerup', handleDragEnd);
    setDragging(false);
    if (!ref.current) return;
    const newX = e.clientX - dragData.current.offsetX;
    const newY = e.clientY - dragData.current.offsetY + window.scrollY;
    const pageW = document.documentElement.scrollWidth || window.innerWidth;
    const pageH = document.documentElement.scrollHeight || window.innerHeight;
    const x_pct = (newX / pageW) * 100;
    const y_pct = (newY / pageH) * 100;
    try {
      await axios.put(`${API}/admin/announcements/${ann.id}/position`, { x_pct, y_pct });
      onRefresh && onRefresh();
    } catch (err) {
      toast.error('Failed to save position');
    }
  };

  const handleResizeStart = (e) => {
    if (!isAdmin || !ref.current) return;
    e.stopPropagation();
    e.preventDefault();
    const rect = ref.current.getBoundingClientRect();
    resizeData.current = {
      startX: e.clientX,
      startY: e.clientY,
      startW: rect.width,
      startH: rect.height,
    };
    setResizing(true);
    window.addEventListener('pointermove', handleResizeMove);
    window.addEventListener('pointerup', handleResizeEnd);
  };

  const handleResizeMove = (e) => {
    if (!ref.current) return;
    const newW = Math.max(220, Math.min(1200, resizeData.current.startW + (e.clientX - resizeData.current.startX)));
    const newH = Math.max(80, Math.min(2000, resizeData.current.startH + (e.clientY - resizeData.current.startY)));
    ref.current.style.width = `${newW}px`;
    ref.current.style.height = `${newH}px`;
  };

  const handleResizeEnd = async (e) => {
    window.removeEventListener('pointermove', handleResizeMove);
    window.removeEventListener('pointerup', handleResizeEnd);
    setResizing(false);
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const pageW = document.documentElement.scrollWidth || window.innerWidth;
    const pageH = document.documentElement.scrollHeight || window.innerHeight;
    const x_pct = (rect.left / pageW) * 100;
    const y_pct = ((rect.top + window.scrollY) / pageH) * 100;
    try {
      await axios.put(`${API}/admin/announcements/${ann.id}/position`, {
        x_pct, y_pct,
        width_px: Math.round(rect.width),
        height_px: Math.round(rect.height),
      });
      onRefresh && onRefresh();
    } catch (err) {
      toast.error('Failed to save size');
    }
  };

  const handleVote = async (idx) => {
    try {
      await axios.post(`${API}/announcements/${ann.id}/vote`, { option_index: idx });
      toast.success('Vote recorded');
      onRefresh && onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to vote');
    }
  };

  const poll = ann.poll;
  const userVoteIdx = poll ? poll.options.findIndex(o => o.votes?.includes(user?.id)) : -1;
  const totalVotes = poll ? poll.options.reduce((s, o) => s + (o.votes?.length || 0), 0) : 0;

  const borderColor = ann.border_color || '#eab308'; // default yellow-500
  const baseClass = "bg-black/80 backdrop-blur-sm border-l-4 border-y border-r border-y-white/10 border-r-white/10 p-4 text-left relative";
  const customWidth = ann.position?.width_px || 360;
  const customHeight = ann.position?.height_px;
  const wrapperStyle = free
    ? {
        position: 'absolute',
        left: `${(ann.position.x_pct / 100) * (document.documentElement.scrollWidth || window.innerWidth)}px`,
        top: `${(ann.position.y_pct / 100) * (document.documentElement.scrollHeight || window.innerHeight)}px`,
        width: `${customWidth}px`,
        height: customHeight ? `${customHeight}px` : 'auto',
        maxWidth: '90vw',
        overflow: customHeight ? 'auto' : 'visible',
        zIndex: dragging || resizing ? 50 : 30,
        cursor: isAdmin ? (dragging ? 'grabbing' : 'auto') : 'default',
        userSelect: dragging || resizing ? 'none' : 'auto',
        borderLeftColor: borderColor,
      }
    : { borderLeftColor: borderColor };

  // Detect if content has HTML markup
  const isHtml = /<[a-z][\s\S]*>/i.test(ann.content || '');

  return (
    <div
      ref={ref}
      className={baseClass}
      style={wrapperStyle}
      data-testid={`announcement-${ann.id}`}
    >
      <div className="flex items-center gap-2 mb-1">
        {isAdmin && free && (
          <button
            onPointerDown={handleDragStart}
            className="text-yellow-500/70 hover:text-yellow-400 cursor-grab active:cursor-grabbing"
            title="Drag to move"
            data-testid={`drag-handle-${ann.id}`}
          >
            <Move className="w-3.5 h-3.5" />
          </button>
        )}
        <Megaphone className="w-4 h-4 text-yellow-500 flex-shrink-0" />
        <h3 className="font-bold text-white text-sm flex-1">{ann.title}</h3>
        {isAdmin && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(ann.id, e); }}
              className="text-red-500/50 hover:text-red-400 transition-colors"
              title="Delete"
              data-testid={`delete-ann-${ann.id}`}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onEdit(ann); }}
              className="text-yellow-500/70 hover:text-yellow-400"
              title="Edit"
              data-testid={`edit-ann-${ann.id}`}
            >
              <Edit3 className="w-3.5 h-3.5" />
            </button>
          </>
        )}
      </div>
      <p className="text-zinc-400 text-sm whitespace-pre-wrap pl-6" data-testid={`ann-content-plain-${ann.id}`} style={{ display: isHtml ? 'none' : undefined }}>{!isHtml ? ann.content : ''}</p>
      {isHtml && (
        <div
          className="text-zinc-300 text-sm pl-6 rte-content"
          dangerouslySetInnerHTML={{ __html: ann.content }}
          data-testid={`ann-content-rich-${ann.id}`}
        />
      )}
      {ann.image_url && (
        <img
          src={`${BASE}${ann.image_url}`}
          alt={ann.title}
          className="mt-3 max-h-64 w-auto object-contain border border-white/10 ml-6"
          data-testid={`announcement-image-${ann.id}`}
        />
      )}
      {poll && poll.question && (
        <div className="mt-4 pl-6 space-y-2" data-testid={`poll-${ann.id}`}>
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-cyan-400 font-bold">
            <BarChart3 className="w-3 h-3" />
            Poll
          </div>
          <p className="text-white text-sm font-medium">{poll.question}</p>
          <div className="space-y-1.5">
            {poll.options.map((opt, idx) => {
              const votes = opt.votes?.length || 0;
              const pct = totalVotes > 0 ? Math.round((votes / totalVotes) * 100) : 0;
              const isUserVote = idx === userVoteIdx;
              return (
                <button
                  key={idx}
                  onClick={() => user ? handleVote(idx) : toast.error('Log in to vote')}
                  disabled={!user}
                  className={`relative w-full text-left px-3 py-2 border ${isUserVote ? 'border-cyan-400 bg-cyan-400/10' : 'border-zinc-800 hover:border-zinc-600 bg-black/40'} transition-colors`}
                  data-testid={`poll-option-${ann.id}-${idx}`}
                >
                  <div className="absolute inset-0 bg-yellow-500/10" style={{ width: `${pct}%` }} />
                  <div className="relative flex items-center justify-between text-sm">
                    <span className={isUserVote ? 'text-cyan-300 font-bold' : 'text-zinc-300'}>{opt.text}</span>
                    <span className="text-zinc-500 text-xs font-mono">{pct}% · {votes}</span>
                  </div>
                </button>
              );
            })}
          </div>
          <p className="text-zinc-600 text-[10px] uppercase tracking-wide">{totalVotes} total {totalVotes === 1 ? 'vote' : 'votes'}</p>
        </div>
      )}
      {isAdmin && free && (
        <button
          onPointerDown={handleResizeStart}
          className="absolute bottom-0 right-0 w-6 h-6 flex items-center justify-center text-yellow-500/70 hover:text-yellow-400 cursor-nwse-resize bg-black/60 border-t border-l border-yellow-500/30"
          title="Drag to resize"
          data-testid={`resize-handle-${ann.id}`}
        >
          <GripHorizontal className="w-3.5 h-3.5 rotate-45" />
        </button>
      )}
    </div>
  );
}
